import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import ChurchUsageCard from './ChurchUsageCard';

type MonthCount = { month: string };

type Overview = {
  content: {
    worshipItems: number;
    sermonVideos: number;
    newsPosts: number;
    galleryAlbums: number;
    galleryPhotosApproved: number;
    galleryPhotosPending: number;
  };
  contentTrend: (MonthCount & { worship: number; sermon: number; news: number })[];
  admin: {
    total: number;
    byRole: { super: number; staff: number };
    byStatus: { active: number; pending: number; rejected: number };
  };
  notification: {
    total: number;
    sent: number;
    failed: number;
    trend: (MonthCount & { count: number })[];
  };
  usage: {
    totalEvents: number;
    appOpens: number;
    screenViews: number;
    activeDevices7d: number;
    activeDevices30d: number;
    topScreens: { screenName: string; count: number }[];
    dailyActiveDevices: { date: string; count: number }[];
  };
};

type AppTypeKey = 'user' | 'admin' | 'roll' | 'finance' | 'sundayschool';

type AppLinkRow = { status: string; buildVersion?: string | null; enabled?: number } | null;

type AppLinkStatus = {
  registered: boolean;
  churchName: string | null;
  apps: Partial<Record<AppTypeKey, AppLinkRow>> | null;
};

const DOT_COLORS = ['#1d4ed8', '#047857', '#b45309', '#be185d', '#6d28d9', '#0f766e'];

const APP_LINK_LABEL: Record<string, string> = {
  connected: '연동됨',
  in_progress: '빌드 진행중',
  pending_build: '빌드 대기',
  suspended: '중지됨',
  not_enabled: '미사용',
};

const APP_LINK_BADGE: Record<string, string> = {
  connected: 'manage-badge--green',
  in_progress: 'manage-badge--blue',
  pending_build: 'manage-badge--gray',
  suspended: 'manage-badge--red',
  not_enabled: 'manage-badge--gray',
};

const APP_TYPE_LABEL: Record<AppTypeKey, string> = {
  user: '성도용 앱',
  admin: '앱관리 앱',
  roll: '교적부 앱',
  finance: '재정 앱',
  sundayschool: '주일학교 앱',
};

function monthLabel(month: string) {
  const parts = month.split('-');
  return `${Number(parts[1])}월`;
}

export default function ManageDashboard({ authChurch, requesterId }: { authChurch: string; requesterId: number }) {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appLink, setAppLink] = useState<AppLinkStatus | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/stats/overview`, { params: { authChurch } })
      .then((res) => setData(res.data?.data ?? null))
      .catch((err) => {
        console.error('GET /stats/overview', err);
        setError('대시보드 데이터를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));

    axios
      .get(`${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}/self`)
      .then((res) => setAppLink(res.data?.data ?? null))
      .catch((err) => console.error('GET /admin-console/church-apps/:authChurch/self', err));
  }, [authChurch]);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">대시보드</h2>
          <p className="manage-panel-desc">
            교회 앱 운영 현황을 한눈에 확인합니다.
            <span className="manage-badge manage-badge--navy" style={{ marginLeft: 8 }}>교회 아이디: {authChurch}</span>
            {appLink && appLink.registered && appLink.apps && (
              (['user', 'admin', 'roll', 'finance', 'sundayschool'] as const).map((appType) => {
                const row = appLink.apps![appType] ?? null;
                // 사용자앱 외에는 선택 앱 - 플랫폼에서 켜 주지 않았거나 중지한 경우 "미사용"
                const optional = appType !== 'user';
                const notEnabled =
                  optional && (!row || Number(row.enabled ?? 1) !== 1);
                const status = notEnabled ? 'not_enabled' : row?.status ?? 'pending_build';
                return (
                  <span
                    key={appType}
                    className={`manage-badge ${APP_LINK_BADGE[status] ?? 'manage-badge--gray'}`}
                    style={{ marginLeft: 6 }}
                    title={row?.buildVersion ? `버전 ${row.buildVersion}` : undefined}
                  >
                    {APP_TYPE_LABEL[appType]} {APP_LINK_LABEL[status] ?? status}
                  </span>
                );
              })
            )}
            {appLink && !appLink.registered && (
              <span className="manage-badge manage-badge--red" style={{ marginLeft: 6 }}>연동된 앱 없음</span>
            )}
          </p>
        </div>
      </div>

      <ChurchUsageCard authChurch={authChurch} requesterId={requesterId} />

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="manage-stats">
            <div className="manage-stat-card">
              <span>담당자 수</span>
              <strong>{data.admin.total}명</strong>
            </div>
            <div className="manage-stat-card">
              <span>최근 7일 활성기기</span>
              <strong>{data.usage.activeDevices7d}대</strong>
            </div>
          </div>

          <div className="manage-grid-2col">
            <div className="manage-side-stack">
              <section className="manage-card-block">
                <div className="manage-block-head">
                  <h3 className="manage-block-title">콘텐츠 현황</h3>
                </div>
                <div className="manage-side-card-body" style={{ padding: 16 }}>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[0] }} />
                    <span className="manage-stat-row-label">예배순서</span>
                    <span className="manage-stat-row-count">{data.content.worshipItems}개</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[1] }} />
                    <span className="manage-stat-row-label">설교영상</span>
                    <span className="manage-stat-row-count">{data.content.sermonVideos}개</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[2] }} />
                    <span className="manage-stat-row-label">교회소식</span>
                    <span className="manage-stat-row-count">{data.content.newsPosts}개</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[3] }} />
                    <span className="manage-stat-row-label">갤러리 앨범</span>
                    <span className="manage-stat-row-count">{data.content.galleryAlbums}개</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[4] }} />
                    <span className="manage-stat-row-label">
                      갤러리 사진
                      {data.content.galleryPhotosPending > 0 && (
                        <span className="manage-stat-row-sub">승인대기 {data.content.galleryPhotosPending}장</span>
                      )}
                    </span>
                    <span className="manage-stat-row-count">{data.content.galleryPhotosApproved}장</span>
                  </div>
                </div>
              </section>

              <section className="manage-card-block">
                <div className="manage-block-head">
                  <h3 className="manage-block-title">월별 콘텐츠 등록 추이</h3>
                </div>
                <div className="manage-mini-chart" style={{ padding: '16px 18px' }}>
                  {data.contentTrend.map((m, index) => {
                    const total = m.worship + m.sermon + m.news;
                    const max = Math.max(1, ...data.contentTrend.map((r) => r.worship + r.sermon + r.news));
                    return (
                      <div className="manage-mini-chart-col" key={m.month}>
                        <div
                          className={`manage-mini-chart-bar${index === data.contentTrend.length - 1 ? ' is-current' : ''}`}
                          style={{ height: `${Math.max(4, (total / max) * 100)}%` }}
                          title={`${monthLabel(m.month)} 예배 ${m.worship} · 설교 ${m.sermon} · 소식 ${m.news}`}
                        />
                        <span className="manage-mini-chart-label">{monthLabel(m.month)}</span>
                      </div>
                    );
                  })}
                </div>
              </section>


            </div>

            <div className="manage-side-stack">
              <div className="manage-side-card">
                <div className="manage-side-card-head">🛡️ 담당자 현황</div>
                <div className="manage-side-card-body">
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[0] }} />
                    <span className="manage-stat-row-label">최종관리자</span>
                    <span className="manage-stat-row-count">{data.admin.byRole.super}명</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[1] }} />
                    <span className="manage-stat-row-label">일반관리자</span>
                    <span className="manage-stat-row-count">{data.admin.byRole.staff}명</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[2] }} />
                    <span className="manage-stat-row-label">승인 대기</span>
                    <span className="manage-stat-row-count">{data.admin.byStatus.pending}명</span>
                  </div>
                </div>
              </div>

              <div className="manage-side-card">
                <div className="manage-side-card-head">📱 인기 화면 TOP {data.usage.topScreens.length}</div>
                <div className="manage-side-card-body">
                  {data.usage.topScreens.length === 0 && <p className="manage-side-empty">아직 수집된 조회 기록이 없습니다.</p>}
                  {data.usage.topScreens.map((s, index) => (
                    <div className="manage-stat-row" key={s.screenName}>
                      <span className="manage-stat-row-dot" style={{ background: DOT_COLORS[index % DOT_COLORS.length] }} />
                      <span className="manage-stat-row-label">{s.screenName}</span>
                      <span className="manage-stat-row-count">{s.count}회</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="manage-side-card">
                <div className="manage-side-card-head">✉️ 알림 발송 현황</div>
                <div className="manage-side-card-body">
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: 'var(--success)' }} />
                    <span className="manage-stat-row-label">발송 성공</span>
                    <span className="manage-stat-row-count">{data.notification.sent}건</span>
                  </div>
                  <div className="manage-stat-row">
                    <span className="manage-stat-row-dot" style={{ background: 'var(--danger)' }} />
                    <span className="manage-stat-row-label">발송 실패</span>
                    <span className="manage-stat-row-count">{data.notification.failed}건</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
